import {
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../api/snackbarSlice';
import React, { useEffect, useState } from 'react';
import { Close } from '@mui/icons-material';
import BusinessProfileSelect from '../../../components/form/BusinessProfileSelect';
import { useForm } from 'react-hook-form';
import { useUpdateShopifyStoreHandler } from '../dataHandlers/updateShopifyStoreHandler';
import { useDisconnectShopifyHandler } from '../dataHandlers/disconnectShopifyHandler';
import { useGetBusinessProfileHandler } from '../dataHandlers/getBusinessProfileHandler';
import ShopifyLogsModal from './ShopifyLogsModal';
import Loader from '../../../components/Loader';

const ShopifyStoreCard = ({ store }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [orderEvent, setOrderEvent] = useState(store.order_event);
  const { businessProfile } = useGetBusinessProfileHandler(store.shop_id, store.business_profile);
  const profileForStore = businessProfile?.[store?.shop_id];

  const reloadPage = () => {
    setOpen(false);
    setLoading(true);
    setTimeout(() => {
      window.location.reload();
    }, 3000);
  };

  const { onSave } = useUpdateShopifyStoreHandler({ onSuccess: reloadPage });
  const { onDisconnect, isPending } = useDisconnectShopifyHandler({ onSuccess: reloadPage });

  const handleClose = () => {
    setOpen(false);
  };

  const handleLogsModalClose = () => {
    setLogsModalOpen(false);
  };

  const onSaveButtonClick = async () => {
    const updatedProfile = watch('store.business_profile');
    if (!updatedProfile) {
      setLoading(false);
      dispatch(showSnackbar({ message: 'Please select a business profile', severity: 'error' }));
      return;
    }

    const payload = {
      business_profile: updatedProfile.profile_id,
      org_ref: store.org_ref,
      shop_id: store.shop_id,
    };
    await onSave(payload);
  };

  const onDisconnectButtonClick = async () => {
    reloadPage();
    await onDisconnect({ shop_id: store.shop_id });
  };

  const checkActiveConnection = !store.connected;

  const {
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: { store: { business_profile: null } },
  });

  useEffect(() => {
    if (profileForStore) {
      reset({
        store: {
          business_profile: profileForStore,
        },
      });
    }
  }, [profileForStore, reset]);

  return (
    <>
      <Grid container spacing={1.5} justifyContent={'space-between'} sx={{ mb: 3 }}>
        <Grid size={12}>
          <Divider />
        </Grid>
        <Grid size={12} item sx={{ display: 'flex', alignItems: 'center' }} justifyContent={'space-between'}>
          <Typography variant="subtitle2" sx={{ fontWeight: 100, minWidth: 120 }}>
            Shopify Store: <strong>{store.shop_id}</strong> &nbsp;
            {!checkActiveConnection && (
              <Chip
                label="Connected"
                color="success"
                size="small"
                variant="outlined"
                sx={{ '& .MuiChip-label': { fontWeight: 'bold' } }}
              />
            )}
            {checkActiveConnection && (
              <Chip
                label="Disconnected"
                color="error"
                size="small"
                variant="outlined"
                sx={{ '& .MuiChip-label': { fontWeight: 'bold' } }}
              />
            )}
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <Tooltip title="Shopify integration logs">
              <Button
                variant="outlined"
                onClick={() => setLogsModalOpen(true)}
                sx={{ minWidth: 50, textTransform: 'none' }}
              >
                <span className="material-symbols-outlined"> browse_activity </span>
              </Button>
            </Tooltip>

            <Button
              variant="outlined"
              onClick={() => setOpen(true)}
              disabled={checkActiveConnection}
              sx={{ minWidth: 140, textTransform: 'none' }}
            >
              {loading ? '...' : 'Disconnect'}
            </Button>
          </Stack>
        </Grid>
        <Grid size={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 100, minWidth: 120 }}>
            Select Business Profile:
          </Typography>
        </Grid>
        <Grid size={6}>
          <Typography variant="subtitle2" sx={{ fontWeight: 100, minWidth: 120 }}>
            Create Invoice On:
          </Typography>
        </Grid>
        <Grid size={6}>
          <BusinessProfileSelect
            label="Business Profile"
            name="store.business_profile"
            control={control}
            errors={errors}
          />
        </Grid>
        <Grid size={6}>
          <FormControl fullWidth sx={{ marginBottom: '10px' }}>
            <InputLabel>Webhook Event</InputLabel>
            <Select
              size="small"
              value={orderEvent || 'NA'}
              disabled={true}
              label="Webhook event"
              onChange={(e) => setOrderEvent(e.target.value)}
            >
              <MenuItem value="ORDERS_CREATE">Order Create</MenuItem>
              <MenuItem value="ORDERS_PAID">Order Paid</MenuItem>
              <MenuItem value="NA">-</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={12}>
          <Button variant="contained" onClick={onSaveButtonClick}>
            Save
          </Button>
        </Grid>
      </Grid>
      <Modal open={open} onClose={handleClose}>
        <Paper
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 700,
            padding: 16,
            boxShadow: 24,
            outline: 'none',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Disconnect Store</Typography>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Stack>
          <Divider />

          <Typography variant="body1" mt={2} mb={2}>
            <ul>
              <li>
                As per Shopify&apos;s documentation, calling the uninstall API is an <b>irreversible operation</b>.
              </li>
              <li>This action will invalidate the app&apos;s access token for the store.</li>
              <li>
                Shopify will automatically handle the cleanup of its own resources, including deleting any registered
                webhooks and script tags.
              </li>
            </ul>
          </Typography>
          <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
            <Button variant="contained" onClick={onDisconnectButtonClick}>
              {loading ? 'Processing...' : 'Continue'}
            </Button>
          </Stack>
        </Paper>
      </Modal>
      <ShopifyLogsModal open={logsModalOpen} onClose={handleLogsModalClose} store={store} />
      <Loader isLoading={loading || isPending} />
    </>
  );
};

export default ShopifyStoreCard;
